import styled, { css } from "styled-components";
import { useMemo, useRef, useState } from "react";
import makeHtmltoImage from "../../API/makePDF";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 9998;
`;

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: white;
  display: flex;
  justify-content: center;
  padding: 20px 40px;
  overflow: auto;
`;

const Modal = styled.div`
  width: min(960px, 100%);
  min-height: 100%;
  display: flex;
  flex-direction: column;

  padding: 32px 28px 28px;

  @media (max-width: 1024px) {
    padding: 28px 22px 22px;
  }

  @media (max-width: 640px) {
    padding: 20px 16px 16px;
  }
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: 640px) {
    margin-bottom: 14px;
  }
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  gap: 6px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.2px;

  @media (max-width: 1024px) {
    font-size: 30px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const SubTitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 16px;
  line-height: 1.45;

  @media (max-width: 1024px) {
    font-size: 16px;
  }

  @media (max-width: 640px) {
    font-size: 16px;
    line-height: 1.55;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 8px;
  font-size: 18px;
  line-height: 1;
  color: #111827;

  &:hover {
    opacity: 0.7;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;

  @media (max-width: 640px) {
    gap: 14px;
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const cardBase = css`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
`;

const ActionCard = styled.button`
  ${cardBase}
  width: 100%;
  padding: 18px 16px;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    border-color: #d1d5db;
    transform: translateY(-1px);
    transition:
      transform 120ms ease,
      border-color 120ms ease;
  }

  &:active {
    transform: translateY(0px);
  }

  @media (max-width: 640px) {
    padding: 14px 14px;
  }
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 40px;
    height: 40px;
  }
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActionTitle = styled.div`
  font-weight: 800;
  color: #111827;
  font-size: 15px;

  @media (max-width: 1024px) {
    font-size: 16px;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const ActionDesc = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;

  @media (max-width: 1024px) {
    font-size: 13px;
  }

  @media (max-width: 640px) {
    font-size: 13px;
    line-height: 1.55;
  }
`;

const PreviewPanel = styled.div`
  ${cardBase}
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 640px) {
    padding: 14px;
    gap: 10px;
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const PreviewTitle = styled.div`
  font-weight: 800;
  color: #111827;
  font-size: 16px;

  @media (max-width: 1024px) {
    font-size: 16px;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const Counter = styled.div`
  color: #6b7280;
  font-size: 14px;

  @media (max-width: 1024px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const ThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 640px) {
    gap: 10px;
  }
`;

const Thumb = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;

  /* ✅ A4 세로 비율 */
  aspect-ratio: 210 / 297;

  max-height: 560px;

  @media (max-width: 640px) {
    max-height: 520px;
  }
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain; /* ✅ 문서 이미지는 contain */
  background: #f9fafb;
  display: block;
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  border: none;
  background: rgba(17, 24, 39, 0.75);
  color: #fff;
  border-radius: 999px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: rgba(17, 24, 39, 0.9);
  }
`;

const Footer = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  @media (max-width: 640px) {
    margin-top: 14px;
    flex-direction: column;
  }
`;

const Button = styled.button<{ $variant?: "primary" | "ghost" }>`
  border-radius: 12px;
  padding: 16px 30px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 18px;

  ${(p) =>
    p.$variant === "ghost"
      ? css`
          background: #ffffff;
          border-color: #e5e7eb;
          color: #111827;

          &:hover {
            border-color: #d1d5db;
          }
        `
      : css`
          background: #111827;
          color: #ffffff;

          &:hover {
            opacity: 0.92;
          }
        `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (max-width: 1024px) {
    font-size: 18px;
  }

  @media (max-width: 640px) {
    width: 100%;
    font-size: 18px;
  }
`;

type LocalImage = {
  file: File;
  url: string;
  id: string;
};

type Props = {
  reNum: string; // ✅ 업로드 URL에 들어갈 reNum
  onClose?: () => void;
  onSaved?: () => void; // 저장 성공 후 콜백 필요하면 사용
  getContractImageList: () => void; // 계약서 이미지 다시 받아오는 함수
  completeReceipt: () => Promise<void>; // 견적서 통인CS 업로드 함수
};

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(new Error("파일을 읽는 중 오류가 발생했습니다."));
    reader.readAsDataURL(file);
  });

const UploadPhotoModalComponent = (props: Props) => {
  const { reNum, getContractImageList, completeReceipt } = props;

  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const [images, setImages] = useState<LocalImage[]>([]);
  const [saving, setSaving] = useState(false);

  const canSave = images.length >= 1 && images.length <= 2 && !saving;

  const subtitleText = useMemo(() => {
    return `계약서 사진은 최대 2장까지 업로드할 수 있어요. (현재 ${images.length}/2)`;
  }, [images.length]);

  const pickFromGallery = () => galleryInputRef.current?.click();
  const takePhoto = () => cameraInputRef.current?.click();

  const appendFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;

    const incoming = Array.from(fileList);
    const mapped: LocalImage[] = incoming.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
        .toString(16)
        .slice(2)}`,
    }));

    setImages((prev) => {
      const merged = [...prev, ...mapped].slice(0, 2);
      const removed = [...prev, ...mapped].slice(2);
      removed.forEach((img) => URL.revokeObjectURL(img.url));
      return merged;
    });
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleSaveToServer = async () => {
    if (!canSave) return;

    try {
      setSaving(true);

      // ✅ File -> DataURL 변환
      const dataUrls = await Promise.all(
        images.map((img) => fileToDataUrl(img.file)),
      );

      // ✅ 1장이면 1장, 2장이면 2장만 전송
      const payload: string[] = dataUrls.slice(0, 2);

      // API 호출 방식 사용
      await makeHtmltoImage._sendImgToServer2(payload, reNum);
      console.log("이미지 전송 완료");

      // await completeReceipt(); // 견적서 통인CS 업로드

      await getContractImageList(); // 이미지 전송후 계약서 이미지 리스트 다시 받아오기
      // 필요하면 여기서 후처리 호출
      // await completeReceipt();

      props?.onSaved?.();
      props?.onClose?.();
    } catch (error: any) {
      console.error(error);
      alert(error?.message ?? error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    images.forEach((img) => URL.revokeObjectURL(img.url));
    props?.onClose?.();
  };

  return (
    <>
      <Backdrop onClick={handleClose} />
      <Wrapper>
        <Modal onClick={(e) => e.stopPropagation()}>
          <Header>
            <TitleArea>
              <Title>계약서 사진 업로드</Title>
              <SubTitle>{subtitleText}</SubTitle>
            </TitleArea>
            <CloseButton onClick={handleClose} aria-label="닫기">
              ✕
            </CloseButton>
          </Header>

          <Content>
            <ActionGrid>
              <ActionCard onClick={takePhoto} type="button">
                <IconCircle>📷</IconCircle>
                <ActionText>
                  <ActionTitle>계약서 촬영</ActionTitle>
                  <ActionDesc>카메라로 바로 촬영해서 업로드</ActionDesc>
                </ActionText>
              </ActionCard>

              <ActionCard onClick={pickFromGallery} type="button">
                <IconCircle>🖼️</IconCircle>
                <ActionText>
                  <ActionTitle>사진첩에서 가져오기</ActionTitle>
                  <ActionDesc>기기에 저장된 사진을 선택</ActionDesc>
                </ActionText>
              </ActionCard>
            </ActionGrid>

            <PreviewPanel>
              <PreviewHeader>
                <PreviewTitle>미리보기</PreviewTitle>
                <Counter>{images.length}/2</Counter>
              </PreviewHeader>

              <ThumbGrid>
                {images.map((img) => (
                  <Thumb key={img.id}>
                    <ThumbImg src={img.url} alt="업로드된 계약서" />
                    <RemoveBtn
                      type="button"
                      onClick={() => removeImage(img.id)}
                    >
                      삭제
                    </RemoveBtn>
                  </Thumb>
                ))}

                {Array.from({ length: Math.max(0, 2 - images.length) }).map(
                  (_, i) => (
                    <Thumb key={`empty-${i}`} />
                  ),
                )}
              </ThumbGrid>
            </PreviewPanel>

            <Footer>
              <Button $variant="ghost" onClick={handleClose} type="button">
                취소
              </Button>
              <Button
                onClick={handleSaveToServer}
                disabled={!canSave}
                type="button"
              >
                {saving ? "저장 중..." : "계약서 업로드"}
              </Button>
            </Footer>
          </Content>

          {/* 숨겨진 input들 */}
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              appendFiles(e.target.files);
              e.currentTarget.value = "";
            }}
          />

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            // ✅ multiple 제거
            style={{ display: "none" }}
            onChange={(e) => {
              appendFiles(e.target.files);
              e.currentTarget.value = "";
            }}
          />
        </Modal>
      </Wrapper>
    </>
  );
};

export default UploadPhotoModalComponent;
